/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useState, useEffect, useContext } from 'react';
import { format } from 'date-fns';
import sanitizeHtml from 'sanitize-html';

import Evolution from '../../components/Evolution/Evolution';
import LineChart from '../../components/LineChart/LineChart';
import Toggler from '../../components/Toggler/Toggler';
import PriceMapChart from '../../components/PriceMapChart/PriceMapChart';
import GasByContryMapChart from '../../components/GasByContryMapChart/GasByContryMapChart';
import StackedAreaChart from '../../components/StackedAreaChart/StackedAreaChart';
import NbmFlowTable from './NbmFlowTable/NbmFlowTable';
import NbpPricesTable from './NbpPricesTable/NbpPricesTable';
import Divider from '../../components/Divider/Divider';
import UkPlannedTable from './UkPlannedTable/UkPlannedTable';
import Loader from '../../components/Loader/Loader';
import Asterix, { AsterixType } from '../../components/Asterix/Asterix';
import GlobalLoader from '../../components/GlobalLoader/GlobalLoader';

import GasService, { GasMapType } from '../../core/services/gas-service';
import overviewCards from '../../core/constants/overview-cards';
import COLORS from '../../core/constants/colors';
import { EvolutionModel } from '../../core/models/evolution';
import { OverviewContext } from '../../core/context/overview-context';
import { GasModel } from '../../core/models/gas';
import { GasMapModel } from '../../core/models/gas-map';
import togglers from '../../core/constants/togglers';

import './GasSection.scss';
import SiteStorageToggler from './SiteStorageToggler/SiteStorageToggler';
import PdfPage from '../../components/PdfPage/PdfPage';
import ColumnAxisChart from '../../components/ColumnAxisChart/ColumnAxisChart';
import CommentsApiService from '../../core/services/comments-api-service';
import { ReductionChartModel } from '../../core/models/reduction-chart';
import ReductionChart from '../../components/ReductionChart/ReductionChart';
import ChartHints from '../../components/ChartHints/ChartHints';

const gasService = new GasService();
const commentsService = new CommentsApiService('');

const GasSection = React.memo(function GasSection(): JSX.Element {
  const gasEvolution = useContext(OverviewContext).nbPspot;
  const gasCard: EvolutionModel = overviewCards.gas.evolution;

  const [gasData, setGasData] = useState<GasModel>({} as GasModel);
  const [gasDayAheadMapData, setGasDayAheadMapData] = useState<GasMapModel>({
    chartGas: [],
    marketsMap: [],
  });
  const [gasMapType, setGasMapType] = useState<GasMapType>(GasMapType.GasDayAheadP);
  const [isLoading, setIsLoading] = useState(true);
  const [mapDataPending, setMapDataPending] = useState(false);

  const [gasToggler, setGasToggler] = useState(togglers.gasAhead);
  const [currencyToggler, setCurrencyToggler] = useState(togglers.therm);

  const [reductionChartData, setReductionChartData] = useState<Array<ReductionChartModel>>();
  const [reductionChartLoading, setReductionChartLoading] = useState<boolean>();

  const reductionChartConfig = {
    series:
      reductionChartData &&
      reductionChartData.map((s) => ({
        name: null, // s.marketName,
        data: s.chart.map((p) => [p.date, p.value]),
      })),
  };

  useEffect(() => {
    gasService
      .getGas()
      .then((response) => {
        setGasData(response);
      })
      .catch(console.error)
      .finally(() => {
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setReductionChartLoading(true);
    gasService
      .getReductionChart()
      .then((response) => {
        setReductionChartData(response);
      })
      .catch(console.error)
      .finally(() => {
        setReductionChartLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setMapDataPending(true);
    gasService
      .getGasMap(gasMapType)
      .then((response) => {
        setGasDayAheadMapData(response);
        setMapDataPending(false);
      })
      .catch((err) => {
        console.error(err);
        setMapDataPending(false);
      });
  }, [gasMapType]);

  useEffect(() => {
    handleMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gasToggler, currencyToggler]);

  function handleMap() {
    if (gasToggler === togglers.gasAhead) {
      if (currencyToggler === togglers.therm) {
        setGasMapType(GasMapType.GasDayAheadP);
      } else if (currencyToggler === togglers.mwh) {
        setGasMapType(GasMapType.GasDayAheadM);
      }
    } else if (gasToggler === togglers.gasM) {
      if (currencyToggler === togglers.mwh) {
        setGasMapType(GasMapType.GasMonthM);
      } else if (currencyToggler === togglers.therm) {
        setGasMapType(GasMapType.GasMonthP);
      }
    }
  }

  const handleGasType = (output: string) => {
    setGasToggler(output);
  };

  const handleCurrencyType = (output: string) => {
    setCurrencyToggler(output);
  };

  const onSave = (data: { value: string; id: number; type: string }) => {
    const sanitizeConf = {
      allowedTags: [],
      allowedAttributes: {},
    };
    const value = sanitizeHtml(data.value, sanitizeConf);
    if (data.id) {
      return commentsService.updateCustomPrice(data.id, data.type, value);
    }
    return commentsService.setCustomPrice(data.type, value).then((response: any) => {
      setGasData({
        ...gasData,
        priceTable: {
          ...gasData.priceTable,
          pricesTable: gasData.priceTable.pricesTable.map((item) => {
            if (item.postIndex === data.type) {
              return {
                ...item,
                idUpdate: response.id,
              };
            }
            return item;
          }),
        },
      });
    });
  };

  const onRemoveCustomPrice = (id: number) => {
    return commentsService.removeCustomPrice(id).then(() => {
      setGasData({
        ...gasData,
        priceTable: {
          ...gasData.priceTable,
          pricesTable: gasData.priceTable.pricesTable.map((item) => {
            if (item.idUpdate === id) {
              return {
                ...item,
                idUpdate: null,
                col4: Number(item.col4),
              };
            }
            return item;
          }),
        },
      });
    });
  };

  const edition = window.sessionStorage.getItem('edition');

  const date1 = gasData?.getNBPCurrentSystemTable?.date1;
  const date2 = gasData?.getNBPCurrentSystemTable?.date2;

  const formatDate = (d: string) => (d ? format(new Date(d), 'dd/MM/yy') : null);

  return isLoading || reductionChartLoading ? (
    <Loader />
  ) : (
    <div className="GasSection" id="gas">
      {mapDataPending ? <GlobalLoader /> : null}
      <div className="total-container">
        {/* <img src={gasIcon} alt="" /> */}
        <PdfPage>
          <h2>
            <span>Gas</span>
          </h2>
          <div className="row mb-8">
            <div className="col-md-6">
              <div className="section-summary mb-4">
                <Evolution
                  arrow={gasEvolution.arrow}
                  price={gasEvolution.marketValue}
                  origin={gasCard.origin}
                  currency={gasCard.currency}
                />

                <Asterix type={AsterixType.basic} />
              </div>

              <LineChart
                title="NBP Front Contracts"
                yAxisTitle="p/therm"
                data={[...gasData.chartNBP]}
                colors={[COLORS.red, COLORS.orange, COLORS.purple, COLORS.blue, COLORS.green]}
              />
              <ChartHints />
            </div>
            <div className="col-md-6">
              <NbpPricesTable
                data={{ ...gasData.priceTable }}
                onEditCell={onSave}
                onRemoveCustomPrice={onRemoveCustomPrice}
              />
            </div>
          </div>

          <div className="GasSection__togglers mb-2">
            <div className="toggle-btn">
              <Toggler
                leftText={togglers.gasAhead}
                rightText={togglers.gasM}
                handler={handleGasType}
              />
            </div>
            <div className="toggle-btn">
              <Toggler
                leftText={togglers.therm}
                rightText={togglers.mwh}
                handler={handleCurrencyType}
              />
            </div>
          </div>

          <div className="row mb-8">
            <div className="col-md-6">
              <PriceMapChart data={[...gasDayAheadMapData.marketsMap]} />
            </div>
            <div className="col-md-6 fcc">
              <div>
                <LineChart
                  title=""
                  yAxisTitle={currencyToggler}
                  data={[...gasDayAheadMapData.chartGas]}
                  colors={[COLORS.red, COLORS.orange, COLORS.purple, COLORS.blue, COLORS.green]}
                />
                <ChartHints />
              </div>
            </div>
          </div>
        </PdfPage>

        <PdfPage>
          <div className="row">
            <div className="col-md-6 mb-8">
              <GasByContryMapChart data={[...gasData.gasStorages]} />
            </div>
            <div className="col-md-6 mb-8">
              <SiteStorageToggler
                percent={[...gasData.chartStorageLevelPercent]}
                mcm={[...gasData.chartStorageLevelMCM]}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-8">
              <NbmFlowTable data={{ ...gasData.getNBPCurrentSystemTable }} />
              <p className="note">
                (1){' '}
                {edition === 'Afternoon'
                  ? `Last update ${formatDate(date1)} at 2 pm`
                  : `Last update ${formatDate(date1)} at 7 am`}
              </p>
              <p className="note">(2) Last update {formatDate(date2)} at 2 pm</p>
              <p className="note">
                (3) A negative figure means that gas is injected into storage, a positive figure
                means that gas is withdrawn
              </p>
            </div>
            <div className="col-md-6 mb-8">
              <StackedAreaChart
                title="UK Import Composition - 7 days view"
                yAxisTitle="mcm/d"
                data={[...gasData.chartImportComposition]}
              />
            </div>
          </div>
        </PdfPage>

        <PdfPage>
          <div className="row">
            <div className="col-lg-6 mb-8">
              <LineChart
                title="Interconnector Gas Flows"
                yAxisTitle="Mcm/d"
                data={[...gasData.chartInterconectorGas]}
                colors={[COLORS.red, COLORS.blue]}
              />
              <ChartHints />
              <p className="note">
                * Positive figures mean that the UK import gas; negative figures mean that the UK
                export gas to the Netherlands via BBL pipeline or to Belgium via IUK pipeline
              </p>
            </div>
            <div className="col-lg-6 mb-8 pl-0">
              {gasData.getUKPlannedTable ? (
                <UkPlannedTable data={[...gasData.getUKPlannedTable]} />
              ) : null}
            </div>
          </div>

          <h3>Aggregated Norwegian Gas Facilities Outages (mcm/d)</h3>

          <div className="mb-8">
            <ReductionChart {...reductionChartConfig} />

            <ChartHints />
          </div>
        </PdfPage>

        <PdfPage>
          {gasData.chartTemperatureUK ? (
            <div className="mb-8">
              <ColumnAxisChart data={[...gasData.chartTemperatureUK]} />
            </div>
          ) : null}
        </PdfPage>

        <Divider />
      </div>
    </div>
  );
});

export default GasSection;
